import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { 
  Cloud, FileText, Store, Calendar, RefreshCw, Filter,
  CheckCircle, XCircle, Loader2, AlertTriangle, Download,
  FolderOpen, Eye, ChevronDown, ChevronUp
} from 'lucide-react';
import { useStoreMapping } from '@/hooks/useStoreMapping';

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Helper to format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy', { locale: pt });
  } catch {
    return dateStr;
  }
};

// Store badge colors
const STORE_COLORS = {
  'NORTESH': 'bg-blue-100 text-blue-700',
  'NORTESHM': 'bg-indigo-100 text-indigo-700',
  'MTS': 'bg-purple-100 text-purple-700',
  'MAIASH': 'bg-green-100 text-green-700',
  'MAIAJU': 'bg-teal-100 text-teal-700',
  'GAIA': 'bg-orange-100 text-orange-700',
  'GAIASH': 'bg-amber-100 text-amber-700',
};

const OneDriveFiles = ({ token }) => {
  const { getStoreName } = useStoreMapping();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filesData, setFilesData] = useState(null);
  const [missingData, setMissingData] = useState(null);
  const [activeTab, setActiveTab] = useState('all-files');
  
  // Filters
  const [storeFilter, setStoreFilter] = useState('all');
  const [showProcessed, setShowProcessed] = useState(true);
  const [showPending, setShowPending] = useState(true);
  const [showUnrecognized, setShowUnrecognized] = useState(true);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  // Expanded stores for missing report
  const [expandedStores, setExpandedStores] = useState({});
  
  // Available stores from data
  const [availableStores, setAvailableStores] = useState([]);
  
  const fetchFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (storeFilter && storeFilter !== 'all') params.append('store_code', storeFilter);
      if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      params.append('show_processed', showProcessed);
      params.append('show_pending', showPending);
      params.append('show_unrecognized', showUnrecognized);
      
      const response = await axios.get(`${API}/onedrive/files?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setFilesData(response.data);
        
        // Extract available stores
        const stores = Object.keys(response.data.summary?.by_store || {});
        setAvailableStores(stores);
      } else {
        setError(response.data.error || 'Erro ao carregar ficheiros');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [token, storeFilter, startDate, endDate, showProcessed, showPending, showUnrecognized]);
  
  const fetchMissingReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (storeFilter && storeFilter !== 'all') params.append('store_code', storeFilter);
      if (startDate) params.append('start_date', format(startDate, 'yyyy-MM-dd'));
      if (endDate) params.append('end_date', format(endDate, 'yyyy-MM-dd'));
      
      const response = await axios.get(`${API}/onedrive/files/missing?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setMissingData(response.data);
      } else {
        setError(response.data.error || 'Erro ao gerar relatório');
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Erro de conexão');
    } finally {
      setLoading(false);
    }
  }, [token, storeFilter, startDate, endDate]);
  
  // Load data on mount and when filters change
  useEffect(() => {
    if (activeTab === 'all-files') {
      fetchFiles();
    } else if (activeTab === 'missing') {
      fetchMissingReport();
    }
  }, [activeTab, fetchFiles, fetchMissingReport]);
  
  const toggleStoreExpand = (store) => {
    setExpandedStores(prev => ({
      ...prev,
      [store]: !prev[store]
    }));
  };
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-blue-500" />
              <CardTitle className="text-lg">Ficheiros OneDrive</CardTitle>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => activeTab === 'all-files' ? fetchFiles() : fetchMissingReport()}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              <span className="ml-2">Atualizar</span>
            </Button>
          </div>
          <CardDescription>
            Lista de ficheiros XML WinTouch disponíveis no OneDrive e estado de processamento
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4 p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 text-slate-500" />
              <Select value={storeFilter} onValueChange={setStoreFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Loja" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as lojas</SelectItem>
                  <SelectItem value="UNRECOGNIZED" className="text-red-600">
                    Apenas Não Reconhecidos
                  </SelectItem>
                  {availableStores.map(store => (
                    <SelectItem key={store} value={store}>{getStoreName(store)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500" />
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-[130px]">
                    {startDate ? format(startDate, 'dd/MM/yyyy') : 'Data início'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
              <span className="text-slate-400">—</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="w-[130px]">
                    {endDate ? format(endDate, 'dd/MM/yyyy') : 'Data fim'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <CalendarComponent
                    mode="single"
                    selected={endDate}
                    onSelect={setEndDate}
                    locale={pt}
                  />
                </PopoverContent>
              </Popover>
              {(startDate || endDate) && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => { setStartDate(null); setEndDate(null); }}
                >
                  Limpar
                </Button>
              )}
            </div>
            
            {activeTab === 'all-files' && (
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={showProcessed} 
                    onChange={(e) => setShowProcessed(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <CheckCircle className="h-3.5 w-3.5 text-green-500" />
                  Processados
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={showPending} 
                    onChange={(e) => setShowPending(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  Pendentes
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={showUnrecognized} 
                    onChange={(e) => setShowUnrecognized(e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  <XCircle className="h-3.5 w-3.5 text-red-500" />
                  Não Reconhecidos
                </label>
              </div>
            )}
          </div>
          
          {/* Error */}
          {error && (
            <div className="p-3 mb-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="all-files" data-testid="tab-all-files">
                <FolderOpen className="h-4 w-4 mr-2" />
                Todos os Ficheiros
              </TabsTrigger>
              <TabsTrigger value="missing" data-testid="tab-missing-files">
                <AlertTriangle className="h-4 w-4 mr-2" />
                Ficheiros em Falta
              </TabsTrigger>
            </TabsList>
            
            {/* All Files Tab */}
            <TabsContent value="all-files" className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-3 text-slate-500">A carregar ficheiros do OneDrive...</span>
                </div>
              ) : filesData ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                    <Card className="bg-slate-50">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-slate-700">
                          {filesData.summary?.total_in_onedrive || 0}
                        </div>
                        <div className="text-sm text-slate-500">Total no OneDrive</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-green-700">
                          {filesData.summary?.processed || 0}
                        </div>
                        <div className="text-sm text-green-600">Processados</div>
                      </CardContent>
                    </Card>
                    <Card className="bg-amber-50">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-amber-700">
                          {filesData.summary?.pending || 0}
                        </div>
                        <div className="text-sm text-amber-600">Pendentes</div>
                      </CardContent>
                    </Card>
                    <Card className={filesData.summary?.unrecognized > 0 ? "bg-red-50 border-red-200" : "bg-slate-50"}>
                      <CardContent className="p-4">
                        <div className={`text-2xl font-bold ${filesData.summary?.unrecognized > 0 ? 'text-red-700' : 'text-slate-400'}`}>
                          {filesData.summary?.unrecognized || 0}
                        </div>
                        <div className={`text-sm ${filesData.summary?.unrecognized > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                          Não Reconhecidos
                        </div>
                      </CardContent>
                    </Card>
                    <Card className="bg-blue-50">
                      <CardContent className="p-4">
                        <div className="text-2xl font-bold text-blue-700">
                          {Object.keys(filesData.summary?.by_store || {}).length}
                        </div>
                        <div className="text-sm text-blue-600">Lojas</div>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Unrecognized Stores Warning */}
                  {Object.keys(filesData.summary?.unrecognized_stores || {}).length > 0 && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <h3 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-2">
                        <XCircle className="h-4 w-4" />
                        Lojas não configuradas encontradas:
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(filesData.summary.unrecognized_stores).map(([store, count]) => (
                          <Badge key={store} className="bg-red-100 text-red-700">
                            {store}: {count} ficheiros
                          </Badge>
                        ))}
                      </div>
                      <p className="text-xs text-red-600 mt-2">
                        Estes ficheiros têm uma loja no nome que não está configurada no sistema.
                        Verifique se o código da loja está correcto ou configure a loja em "Config. Lojas".
                      </p>
                    </div>
                  )}
                  
                  {/* Store Breakdown */}
                  {Object.keys(filesData.summary?.by_store || {}).length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-sm font-medium text-slate-700 mb-2">Por Loja (reconhecidas):</h3>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(filesData.summary.by_store).map(([store, stats]) => (
                          <div key={store} className={`px-3 py-1.5 rounded-lg text-sm ${STORE_COLORS[store] || 'bg-slate-100 text-slate-700'}`}>
                            <span className="font-medium">{getStoreName(store)}</span>
                            <span className="ml-2 opacity-75">
                              {stats.processed}/{stats.total}
                              {stats.pending > 0 && (
                                <span className="ml-1 text-amber-600">({stats.pending} pendentes)</span>
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Files Table */}
                  <div className="border rounded-lg overflow-hidden overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="w-[110px]">Estado</TableHead>
                          <TableHead className="min-w-[400px]">Ficheiro</TableHead>
                          <TableHead className="w-[90px]">Loja</TableHead>
                          <TableHead className="w-[90px]">Data</TableHead>
                          <TableHead className="w-[70px]">Tamanho</TableHead>
                          <TableHead className="w-[180px]">Batch/ISL / Erro</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filesData.files?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                              Nenhum ficheiro encontrado com os filtros aplicados
                            </TableCell>
                          </TableRow>
                        ) : (
                          filesData.files?.map((file, idx) => (
                            <TableRow 
                              key={idx} 
                              className={
                                !file.is_recognized ? 'bg-red-50/50' :
                                file.is_processed ? '' : 'bg-amber-50/50'
                              }
                            >
                              <TableCell>
                                {!file.is_recognized ? (
                                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Não Reconhecido
                                  </Badge>
                                ) : file.is_processed ? (
                                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Processado
                                  </Badge>
                                ) : file.ingestion_error ? (
                                  <Badge className="bg-red-100 text-red-700 hover:bg-red-100" data-testid={`onedrive-file-error-badge-${file.business_date}`}>
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Erro na leitura
                                  </Badge>
                                ) : (
                                  <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                                    <AlertTriangle className="h-3 w-3 mr-1" />
                                    Pendente
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-slate-400 flex-shrink-0" />
                                  <span className="font-mono text-xs break-all" title={file.filename}>
                                    {file.filename}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                <Badge className={STORE_COLORS[file.store_code] || 'bg-slate-100 text-slate-700'}>
                                  {file.store_code ? getStoreName(file.store_code) : '-'}
                                </Badge>
                              </TableCell>
                              <TableCell className="font-mono text-sm">
                                {formatDate(file.business_date)}
                              </TableCell>
                              <TableCell className="text-sm text-slate-500">
                                {formatFileSize(file.size_bytes)}
                              </TableCell>
                              <TableCell>
                                {file.batch_info ? (
                                  <div className="text-xs">
                                    <div className="font-mono text-slate-600">{file.batch_info.batch_id?.slice(0, 20) || '-'}</div>
                                    {file.batch_info.sage_isl && (
                                      <div className="text-green-600">{file.batch_info.sage_isl}</div>
                                    )}
                                  </div>
                                ) : file.recognition_error ? (
                                  <div className="text-xs text-red-600">
                                    {file.recognition_error}
                                  </div>
                                ) : file.ingestion_error ? (
                                  <div className="text-xs text-red-600 max-w-xs" data-testid={`onedrive-file-error-${file.business_date}`}>
                                    {file.ingestion_error.error}
                                    <div className="text-[10px] text-slate-400">
                                      {file.ingestion_error.attempts} tentativa(s) · última {file.ingestion_error.failed_at ? new Date(file.ingestion_error.failed_at).toLocaleString('pt-PT') : ''}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : null}
            </TabsContent>
            
            {/* Missing Files Tab */}
            <TabsContent value="missing" className="mt-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="ml-3 text-slate-500">A analisar ficheiros em falta...</span>
                </div>
              ) : missingData ? (
                <>
                  {/* Date Range Info */}
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg text-sm">
                    <span className="font-medium">Período analisado:</span>{' '}
                    {missingData.date_range?.from} a {missingData.date_range?.to}
                    <span className="ml-4 text-slate-500">
                      ({missingData.total_expected_dates} dias esperados, excluindo Domingos)
                    </span>
                  </div>
                  
                  {/* Stores List */}
                  <div className="space-y-3">
                    {Object.entries(missingData.stores || {}).map(([storeCode, store]) => (
                      <Card key={storeCode} className={store.total_missing > 0 ? 'border-amber-200' : 'border-green-200'}>
                        <CardHeader className="py-3 cursor-pointer" onClick={() => toggleStoreExpand(storeCode)}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge className={STORE_COLORS[storeCode] || 'bg-slate-100 text-slate-700'}>
                                {storeCode}
                              </Badge>
                              <span className="font-medium">{store.store_name}</span>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-sm">
                                <span className="text-green-600 font-medium">{store.total_present}</span>
                                <span className="text-slate-400"> / </span>
                                <span className="text-slate-600">{store.total_expected}</span>
                                <span className="ml-2 text-slate-400">
                                  ({store.coverage_percent}%)
                                </span>
                              </div>
                              {store.total_missing > 0 && (
                                <Badge className="bg-amber-100 text-amber-700">
                                  {store.total_missing} em falta
                                </Badge>
                              )}
                              {expandedStores[storeCode] ? (
                                <ChevronUp className="h-4 w-4 text-slate-400" />
                              ) : (
                                <ChevronDown className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                          </div>
                        </CardHeader>
                        
                        {expandedStores[storeCode] && store.total_missing > 0 && (
                          <CardContent className="pt-0">
                            <div className="border-t pt-3">
                              <h4 className="text-sm font-medium text-slate-700 mb-2">Datas em falta:</h4>
                              
                              {/* By Month */}
                              {Object.entries(store.missing_by_month || {}).map(([month, dates]) => (
                                <div key={month} className="mb-3">
                                  <div className="text-xs font-medium text-slate-500 mb-1">{month}</div>
                                  <div className="flex flex-wrap gap-1">
                                    {dates.map(date => (
                                      <Badge 
                                        key={date} 
                                        variant="outline" 
                                        className="bg-amber-50 text-amber-700 border-amber-200 text-xs"
                                      >
                                        {date.slice(8)}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    ))}
                  </div>
                  
                  {Object.keys(missingData.stores || {}).length === 0 && (
                    <div className="text-center py-12 text-slate-500">
                      Nenhuma loja encontrada no período selecionado
                    </div>
                  )}
                </>
              ) : null}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default OneDriveFiles;
